// How a group's Greenback bill travels (PRD 112 §4.1). Its own file, not
// businessApi.ts, so a change here does not make every test that imports the
// hub file a related test.
//
// THE RAIL IS PER GROUP. `stripe` is today's card subscription; `invoice` is a
// group Greenback bills by invoice instead, because a business in it is in an
// industry the card networks will not serve, or because it holds a module that
// is billed by invoice. Core billing reads only these generic fields and never
// names the module or the industry that put a group there.
//
// EVERY FIELD IS OPTIONAL ON PURPOSE. The web merges before the api that sends
// them, so an absent `rail` must read as today's card rail: the billing card
// then renders exactly as it did before this file existed.

import type { BusinessBilling, BusinessBillingAlert, BusinessBillingModule, BusinessGroupAddition } from './businessApi';

/** How this group's Greenback bill travels. Absent from an older api = today's card rail. */
export type BillingRail = 'stripe' | 'invoice';

/** Who pays this group's bill: the group itself, or the firm that serves it. */
export type BillingPaidBy = 'group' | 'firm';

export interface BusinessBillingRailFields {
  rail?: BillingRail;
  /**
   * The group's platform invoicing account (PRD 112 Wave 2): its invoices, how
   * to pay them, the billing contact and the terms. Sent only for a group with
   * an account, so a card payer, a comp-only group and any api before Wave 2
   * send none, and the card renders as it did without it. See hasInvoicing.
   */
  invoicing?: BillingInvoicing;
  /** Null when nobody pays yet. Absent from an older api. */
  paid_by?: BillingPaidBy | null;
  /**
   * The group's own card subscription is paused in Stripe: it still exists
   * and can charge again, but Stripe is collecting nothing for it, so it gives
   * no access and `subscribed` is false. Sent true only while the books are
   * read-only because of it; a firm, the owner's own Business subscription or
   * a comp that keeps them writable sends false. Absent from an older api,
   * which reads as not paused.
   */
  paused?: boolean;
}

/**
 * GET /business/billing, with the rail fields PRD 112 Wave 1 adds and the
 * module fields Wave 3a adds (BillingModuleInvoiceFields, below; all optional).
 */
export type BusinessBillingWithRail = Omit<BusinessBilling, 'modules'> & BusinessBillingRailFields & {
  modules: BusinessBillingModuleWithInvoice[];
};

// Refusal codes the billing rail adds (PRD 112 §6). Compared against
// `BusinessApiError.code`; the message shown is always the server's, because
// it names the rule.

/** Checkout, the portal or a module change on a group billed by invoice (409). */
export const INVOICE_BILLED = 'invoice_billed';
/** A move to a firm-paid engagement on a group billed by invoice, or about to be (409). */
export const INVOICE_PAYER_CHANGE_REQUIRED = 'invoice_payer_change_required';

/**
 * Whether Greenback bills this group by invoice. False for an absent or
 * unrecognised rail, so an older or newer api never hides the card controls by
 * accident.
 */
export const isInvoiceRail = (billing: BusinessBillingRailFields | null | undefined): boolean =>
  billing?.rail === 'invoice';

/**
 * Whether the group's own card subscription is paused in Stripe. False for an
 * absent field, so an older api never hides Subscribe by accident.
 */
export const isPausedBilling = (billing: BusinessBillingRailFields | null | undefined): boolean =>
  billing?.paused === true;

// ---------------------------------------------------------------------------
// PRD 112 Wave 2: platform invoices. The billing entity (§4.17) issues an
// invoice-rail group's invoices, and the owner pays them by ACH credit or wire
// from their own bank. Nothing here pays, takes a card or asks for bank details:
// the web only shows what the api decided.
//
// MONEY IS INTEGER CENTS and DATES ARE PLATFORM DATES (YYYY-MM-DD, Eastern), so
// they go through components/business/settings/billing/invoiceFormat, never
// `new Date("YYYY-MM-DD")`.
// ---------------------------------------------------------------------------

export type PlatformInvoiceKind = 'first' | 'regular' | 'reactivation' | 'switch' | 'adjustment';

export type PlatformInvoiceStatus = 'open' | 'paid' | 'void' | 'uncollectible';

/** One platform invoice, as the billing card lists it. */
export interface PlatformInvoiceSummary {
  id: string;
  /** The issuer's number, e.g. "GB-000123". */
  number: string;
  kind: PlatformInvoiceKind;
  status: PlatformInvoiceStatus;
  period_start: string;
  period_end: string;
  issued_on: string;
  /** The effective due date: an extension's date when there is one. */
  due_on: string;
  /** The due date was extended by Greenback. */
  extended: boolean;
  total_cents: number;
  balance_cents: number;
  /** Today is after `due_on` and a balance is left. */
  past_due: boolean;
  window_days: number;
  /** Bank debits arrive with Wave 3b; null until then. */
  debit_on: string | null;
  debit_status: string | null;
  return_reason: string | null;
  /** Null for anyone who may not download it (only the owner of a group payer may). */
  pdf_path: string | null;
  /**
   * PRD 112 Wave 3b: the api says this invoice can be paid by card now
   * (`pay_by_card.available`, open, a balance left). Absent from an older api,
   * which reads as false. See invoiceCardPayable.
   */
  card_payable?: boolean;
  /** The attempt still live on it, if any (Wave 3b). Absent or null: none. */
  live_card_attempt?: { id: string; status: CardAttemptStatus } | null;
  /** The latest standing card payment on it (Wave 3b), masked. Absent or null: none. */
  paid_by_card?: PaidByCard | null;
}

/** The billing entity's terms, verbatim, and whether this payer has accepted them. */
export interface BillingTerms {
  version: string;
  /** Rendered verbatim with the issuer's legal name. Shown as written, never reflowed. */
  text: string;
  issuer_name: string;
  accepted: boolean;
  accepted_at: string | null;
  /**
   * True only for an account that has accepted nothing (the first-visit
   * prompt). An acceptance of an older version (v1) still stands for ongoing
   * billing, so `required` is false while `accepted` is false: restart and the
   * owner's move then ask for the current version in the act
   * (CONTRACT-CHANGES-3a item 12).
   */
  required: boolean;
  /**
   * What the account accepted ("v1", "v1.1"), or null. `version` and `text`
   * are always the current version, which is what is sent back. Absent from
   * an older api.
   */
  accepted_version?: string | null;
}

/** Where to send the money: only for the owner of a group payer. */
export interface FullRemittanceDetails {
  bank_name: string;
  account_name: string;
  ach_routing: string;
  wire_routing: string;
  account_number: string;
  bank_address: string;
  verify_phone: string;
  /** Which set of bank details this is, e.g. "2026-10". */
  set: string;
  /** "Put GB-000123 in the payment's memo or addenda." */
  memo_hint: string;
  /** Verbatim, with the phone to verify a change of bank details on. */
  fraud_line: string;
}

/** Everyone else sees the bank masked: "Bank ••••4821, set 2026-10". */
export interface MaskedRemittanceDetails {
  masked: string;
  fraud_line: string;
}

export type RemittanceDetails = FullRemittanceDetails | MaskedRemittanceDetails;

/** Whether the api sent the full bank details (the owner of a group payer) or the masked line. */
export const isFullRemittance = (remittance: RemittanceDetails): remittance is FullRemittanceDetails =>
  typeof (remittance as FullRemittanceDetails).account_number === 'string';

/**
 * Where the payer stands. The api decides it (the first match wins, in this
 * order): closed, cancelled, lapsed, past_due, awaiting_first_payment, due,
 * current. A value this web does not know reads as nothing to say.
 */
export type BillingInvoicingStatus =
  | 'awaiting_first_payment'
  | 'current'
  | 'due'
  | 'past_due'
  | 'lapsed'
  | 'cancelled'
  | 'closed';

/** The reactivation invoice "Restart billing" would issue, priced by the api. */
export interface BillingRestartQuote {
  total_cents: number;
  lines: { description: string; amount_cents: number }[];
}

/** GET /business/billing's `invoicing` block (PRD 112 Wave 2). */
export interface BillingInvoicing {
  account_id: string;
  /** The billing entity's legal name: who issues the invoices (item 33). */
  issuer_name: string;
  status: BillingInvoicingStatus;
  paid_through: string | null;
  /** Null when cancelled, closed or lapsed. */
  next_invoice_on: string | null;
  /** The last writable day once a payment window has started, else null. */
  lapse_on: string | null;
  /** What a cancel today would end access at; sent back on cancel. */
  ends_on: string | null;
  /** Open invoices, the oldest due first. */
  open_invoices: PlatformInvoiceSummary[];
  /** The last 12 paid, void or uncollectible, newest first. */
  recent_invoices: PlatformInvoiceSummary[];
  unapplied_credit_cents: number;
  billing_email: string;
  billing_cc_emails: string[];
  /** Mail to the billing email bounced. */
  email_undeliverable: boolean;
  terms: BillingTerms;
  /** "push" in Wave 2: the payer sends the money. "debit" arrives with Wave 3b. */
  collection: string;
  /** Null when the billing entity's bank details are not configured. */
  remittance: RemittanceDetails | null;
  /** The owner of a group payer: accepts terms, edits the contact, cancels, restarts, downloads. */
  can_manage: boolean;
  /** Lapsed (or closed after a cancel), nothing past due, and the owner. */
  can_restart: boolean;
  /** Null unless `can_restart`. */
  restart_quote: BillingRestartQuote | null;
  /**
   * The group's card subscription with Greenback, while it runs out after a
   * move to invoicing (PRD 112 Wave 3a): the card paid for the days up to its
   * period end, and the billing entity invoices from the day after. Null once
   * it has ended, and for a group that never had a card. Absent from an api
   * before Wave 3a, which reads as null.
   */
  card?: BillingCardTail | null;
  /**
   * Paying an invoice by card on the gateway's hosted page (PRD 112 Wave 3b,
   * §4.18). Not the `card` above, which is the Stripe subscription's tail
   * (PLAN-3b D18). Absent from an api before 3b, which reads as unavailable,
   * so no "Pay by card" button can appear before the api that takes it.
   */
  pay_by_card?: BillingPayByCard | null;
}

/**
 * How a switched group's card subscription ends (PLAN-3a §5.1): at its period
 * end (`period_end`), or at once, for a card that was past due when an admin
 * moved it (`ended_now`). A status this web does not know reads as nothing to say.
 */
export type BillingCardTailStatus = 'period_end' | 'ended_now';

/** `invoicing.card` (PLAN-3a §5.1). */
export interface BillingCardTail {
  /** The instant the card subscription ends. */
  ends_at: string;
  /** The last platform day (YYYY-MM-DD, Eastern) the card paid for. */
  last_day_on: string;
  status: BillingCardTailStatus;
}

/**
 * Whether the card should show the invoice branch: an invoicing block on a
 * group on the invoice rail. Both, so an inconsistent payload never draws
 * invoices beside the card controls.
 */
export const hasInvoicing = (
  billing: BusinessBillingRailFields | null | undefined
): billing is BusinessBillingRailFields & { invoicing: BillingInvoicing } =>
  isInvoiceRail(billing) && billing?.invoicing != null;

/** POST /business/billing/restart's answer: the reactivation invoice and the refreshed billing. */
export interface BillingRestartResult {
  invoice: PlatformInvoiceSummary;
  billing: BusinessBillingWithRail;
}

/**
 * The business payload's billing alert, with the fields Wave 2 adds to an
 * alert read from an invoice row. Absent on a card alert, so a card alert
 * reads exactly as before.
 */
export type BusinessBillingAlertRail = BusinessBillingAlert & {
  rail?: 'invoice';
  /** The oldest past-due invoice's number, when there is one. */
  invoice_number?: string | null;
  /** The owner cancelled billing and its access has ended: lapsed, but not unpaid. */
  cancelled?: boolean;
};

/** An alert about Greenback's invoice, not a card. False for an absent or unknown rail. */
export const isInvoiceAlert = (alert: BusinessBillingAlertRail | null | undefined): boolean =>
  alert?.rail === 'invoice';

// Refusal codes of the owner's invoice endpoints (PRD 112 Wave 2, PLAN-W2 §2).
// Compared against `BusinessApiError.code`. Each is handled by name where it
// can arrive; the sentence shown is the server's, which names the rule.

/** Not the owner of a group payer (403). */
export const BILLING_FORBIDDEN = 'forbidden';
/** The group has no platform billing account (404). */
export const NO_BILLING_ACCOUNT = 'no_billing_account';
/** The terms, the quoted total or the end date shown are no longer current (409); re-read. */
export const TERMS_CHANGED = 'terms_changed';
/** A cancel or restart sent without what the owner was shown (422). */
export const ACKNOWLEDGEMENT_REQUIRED = 'acknowledgement_required';
/** Billing is already cancelled (409). */
export const ALREADY_CANCELLED = 'already_cancelled';
/** A firm pays for this group; the owner does not manage its billing (409). */
export const FIRM_BILLED = 'firm_billed';
/** Restart on a group whose access has not lapsed (409). */
export const NOT_LAPSED = 'not_lapsed';
/** Restart while an invoice is still past due (409; `details.invoice_numbers`). */
export const PAST_DUE_OUTSTANDING = 'past_due_outstanding';
/** The billing entity's record is not configured, so nothing can be issued (409). */
export const ISSUER_NOT_CONFIGURED = 'issuer_not_configured';
/** The billing entity's bank details are not configured (409). */
export const REMITTANCE_NOT_CONFIGURED = 'remittance_not_configured';
/** Something on the invoice has no price (422; `details.key`). */
export const UNPRICED_LINE = 'unpriced_line';
/** Issue on a cancelled account (409). The admin's issue-now; listed so the code is known. */
export const ACCOUNT_CANCELLED = 'account_cancelled';
/** Issue on a closed account (409). The admin's issue-now; listed so the code is known. */
export const ACCOUNT_CLOSED = 'account_closed';
/** Restart: the group this account billed no longer exists (409). Greenback's to sort out. */
export const PAYER_GONE = 'payer_gone';
/** Restart: every day it would bill is already invoiced (409), e.g. something was invoiced after the quote. */
export const NOTHING_TO_INVOICE = 'nothing_to_invoice';
/** Restart: another row still holds a key the restart would start (409). Greenback's to sort out. */
export const PREDECESSOR_ACTIVE = 'predecessor_active';
/** Adding a business to a group whose invoiced billing is cancelled (409): restart billing first. */
export const BILLING_CANCELLED = 'billing_cancelled';
/** A billing email or cc that is not an address (422; `details.field`). */
export const INVALID_EMAIL = 'invalid_email';
/** More than three cc addresses (422). */
export const TOO_MANY_CC = 'too_many_cc';

/** Most cc addresses an invoice goes to (item 8: a billing email plus up to 3 cc). */
export const BILLING_CC_LIMIT = 3;

/**
 * The refusals that mean Greenback's side is not set up, which the owner
 * cannot fix: the restart says to contact support rather than retry.
 * `payer_gone` and `nothing_to_invoice` too (the api's Part 3 contract
 * entry); `predecessor_active` is a row only Greenback can end.
 */
export const NOT_SET_UP_REFUSALS: readonly string[] = [
  ISSUER_NOT_CONFIGURED,
  REMITTANCE_NOT_CONFIGURED,
  UNPRICED_LINE,
  PAYER_GONE,
  NOTHING_TO_INVOICE,
  PREDECESSOR_ACTIVE,
];

/**
 * Of those, the ones a quote can race into (something was invoiced, or the
 * group went, after the card was drawn): the card re-reads after them, so a
 * stale Restart button goes.
 */
export const RESTART_RACE_REFUSALS: readonly string[] = [PAYER_GONE, NOTHING_TO_INVOICE];

// meta.group_additions on the invoice rail (PRD 112 Wave 2, plan D3). The
// api sends `covered_by: "invoice"` for a group Greenback invoices: the added
// business is billed on the group's next invoice from the day it joins, and
// no card or subscription is involved. Typed here, not in businessApi.ts.

/** `covered_by` of a group addition, with the invoice rail. */
export type GroupAdditionCoveredBy = BusinessGroupAddition['covered_by'] | 'invoice';

export const COVERED_BY_INVOICE = 'invoice';

/** Whether this offer is billed on the group's invoices. False for anything else, an older api included. */
export const isInvoicedAddition = (group: Pick<BusinessGroupAddition, 'covered_by'>): boolean =>
  (group.covered_by as GroupAdditionCoveredBy) === COVERED_BY_INVOICE;

/**
 * A refusal's `details` as the invoice endpoints send them: an object
 * (`{ invoice_numbers }`, `{ ends_on }`, `{ current_version }`, `{ field }`,
 * `{ key }`), not the field-error list other endpoints send. Empty when absent.
 */
export const refusalDetails = (details: unknown): Record<string, unknown> =>
  details && typeof details === 'object' && !Array.isArray(details) ? (details as Record<string, unknown>) : {};


// ---------------------------------------------------------------------------
// PRD 112 Wave 3a, slice 3a-2: module changes on the invoice rail (§4.5,
// PLAN-3a §5.2, D10–D12). The owner of an invoiced group adds and removes
// modules herself; the billing entity bills an added module from today as
// catch-up on her next invoice, and a removed one stays until the end of the
// period already invoiced. Nothing is credited.
//
// EVERY FIELD IS OPTIONAL, AND ABSENT MEANS NO CONTROL. An api before 3a-2
// sends none of them, so the invoice branch draws no box at all, exactly as
// in Wave 2; the web merges ahead of its api inert, never sending a module
// set the api would read as a removal.
// ---------------------------------------------------------------------------

/** Why a module on the invoice cannot be removed (D12). */
export type ModuleRemovalBlock = 'industry_exit_required' | 'module_requires';

/**
 * What the App Shop offers for an off-Stripe module (D12): `move` (the group
 * pays its own card subscription, holds no comp; the owner's switch, 3a-3),
 * `add` (already on the invoice rail with an open account), `accept_terms`
 * (as `add`, but the owner has not accepted the terms: billing asks first;
 * CONTRACT-CHANGES-3a item 15), `contact` (everything else: an admin moves
 * it). Null when it is not purchasable, so the Coming soon card stays.
 */
export type ModuleInvoiceOffer = 'move' | 'add' | 'accept_terms' | 'contact';

/** The fields slice 3a-2 adds to each of GET /business/billing's `modules[]`. */
export interface BillingModuleInvoiceFields {
  /** Released and fully priced for this group on the invoice rail (D10, Q5: enforced not required). */
  purchasable?: boolean;
  /**
   * On the group's invoice and renewing: what a declarative PATCH must send
   * back to keep. A comp-held module, or one removed and running out, is not.
   */
  on_invoice?: boolean;
  /** False when removing it would be refused. */
  removable?: boolean;
  removal_block?: ModuleRemovalBlock | null;
  /** Off-Stripe modules only; null or absent for every other. */
  invoice_offer?: ModuleInvoiceOffer | null;
  /**
   * Held by a Greenback comp, not paid for: nothing to add or remove until it
   * ends (3a review #5; the api sends `purchasable: false` with it). Absent
   * from an api that does not send it yet.
   */
  comped?: boolean;
}

/** One module line of GET /business/billing, with the invoice-rail fields. */
export type BusinessBillingModuleWithInvoice = BusinessBillingModule & BillingModuleInvoiceFields;

/** A module the change adds: its price and when it is first billed (PLAN-3a §5.2). */
export interface ModuleChangeAdded {
  key: string;
  name: string;
  monthly_cents: number;
  /** The platform date it is billed from (today). */
  starts_on: string;
  /** The platform date of the invoice that first carries it. */
  first_invoiced_on: string;
}

/** A module the change removes: the instant its last invoiced period ends (UTC; null if the api has none). */
export interface ModuleChangeRemoved {
  key: string;
  name: string;
  held_until: string | null;
}

/** A reason the change would be refused, listed by the preview rather than raised. */
export interface ModuleChangeRefusal {
  code: string;
  message: string;
  key: string | null;
}

/** GET /business/billing/modules/preview (PLAN-3a §5.2, D11). Pure: it writes nothing. */
export interface ModuleChangePreview {
  added: ModuleChangeAdded[];
  removed: ModuleChangeRemoved[];
  /** The acknowledgement the PATCH echoes while anything is added. */
  monthly_increase_cents: number;
  monthly_total_cents_before: number;
  monthly_total_cents_after: number;
  refusals: ModuleChangeRefusal[];
}

/** PATCH /business/billing/modules on the invoice rail: what changed, and the refreshed billing. */
export interface ModuleChangeResult {
  added: unknown[];
  removed: unknown[];
  billing: BusinessBillingWithRail;
}

// Refusal codes of the invoice-rail module change (PLAN-3a §5.2). The
// sentence shown is always the server's: it names the module and the rule.

/** Removing the module that set a business up as restricted (422). */
export const INDUSTRY_EXIT_REQUIRED = 'industry_exit_required';
/** A module without the one it stacks on (422). */
export const MODULE_REQUIRES = 'module_requires';
/** Not released or not fully priced for this group (422). */
export const NOT_PURCHASABLE = 'not_purchasable';
/**
 * The group pays by card, so the invoice rail's module change means nothing
 * (409 from the modules preview and PATCH; CONTRACT-CHANGES-3a item 5).
 */
export const CARD_BILLED = 'card_billed';
/** The invoice account's stint lapsed: no current Business row (409, item 6). */
export const ACCOUNT_LAPSED = 'account_lapsed';
/** The owner has not accepted the billing terms (409 from the modules preview and PATCH; item 16). */
export const TERMS_REQUIRED = 'terms_required';

/** Whether this module line carries the invoice-rail fields at all: an api before 3a-2 sends none. */
export const hasInvoiceModuleFields = (module: BillingModuleInvoiceFields): boolean =>
  typeof module.purchasable === 'boolean' || typeof module.on_invoice === 'boolean';

// ---------------------------------------------------------------------------
// PRD 112 Wave 3a, slice 3a-3: the owner's App Shop switch (§4.1.1, PLAN-3a
// §5.3, D13–D15). The owner of a group that pays its own card subscription
// adds an off-Stripe module and moves the group to invoicing in one step: her
// card runs out its period with nothing charged or refunded, the billing
// entity issues the switch invoice, and she accepts its terms in the same
// request. It never goes back.
// ---------------------------------------------------------------------------

/** GET /business/billing/move_to_invoice?module=<key>: the preview. Pure. */
export interface MoveToInvoicePreview {
  module: { key: string; name: string; monthly_cents: number };
  card: {
    subscription_status: string;
    /** The instant the card subscription ends. */
    ends_at: string;
    /** The last platform day it paid for. */
    last_day_on: string;
    /** "period_end" on the owner's path (a past-due card is refused, D4). */
    path: string;
  };
  /** The billing entity's legal name: who invoices from now on. */
  issuer_name: string;
  invoice: {
    kind: 'switch';
    issued_on: string;
    due_on: string;
    /** Periods are platform dates; null for a line with no period. */
    lines: { description: string; period_start: string | null; period_end: string | null; amount_cents: number }[];
    total_cents: number;
  };
  monthly_total_cents_before: number;
  monthly_total_cents_after: number;
  /** The acknowledgement the POST echoes. */
  monthly_increase_cents: number;
  /** Verbatim: "This business stays on invoicing from <issuer> even if you remove <Module> later." */
  stays_on_invoicing: string;
  /** The billing entity's terms, verbatim, accepted in the same request. */
  terms: { version: string; text: string };
}

/** POST /business/billing/move_to_invoice: the switch invoice and the refreshed billing (201). */
export interface MoveToInvoiceResult {
  invoice: { id: string; number: string; total_cents: number; due_on: string };
  billing: BusinessBillingWithRail;
}

// The switch's refusals (PLAN-3a §5.3). Every one comes before the first
// Stripe call, and the preview refuses with the same codes, so the dialog
// says why before anyone confirms. The sentence shown is the server's.

/** A comped or never-subscribed group: an admin moves it (409). */
export const NO_CARD_SUBSCRIPTION = 'no_card_subscription';
/** The card is paused or incomplete: fixed in Manage billing first (409). */
export const CARD_SUBSCRIPTION_UNSETTLED = 'card_subscription_unsettled';
/** The card is past due: brought up to date in Manage billing first (409, D4/Q2). */
export const CARD_PAST_DUE = 'card_past_due';
/** The module is not billed off Stripe (422). */
export const OFF_STRIPE_MODULE_REQUIRED = 'off_stripe_module_required';
/** Stripe could not be reached; nothing changed (502). */
export const STRIPE_UNREACHABLE = 'stripe_unreachable';
/**
 * The card customer holds a Stripe credit (409, `details.credit_cents`;
 * CONTRACT-CHANGES-3a item 8, PLAN Q3): support refunds it before the move,
 * so the owner is routed to support, never offered the move.
 */
export const STRIPE_CREDIT_BALANCE = 'stripe_credit_balance';
/**
 * The switch invoice is not the one the owner was shown (409, item 14;
 * `details: { total_cents, issued_on, due_on }`): re-read and ask again.
 */
export const QUOTE_CHANGED = 'quote_changed';
/**
 * The group holds a priced comp the move would start billing (409, item 14;
 * `details.keys`, catalogue keys, "business" for Business): support moves it.
 */
export const COMP_WOULD_CONVERT = 'comp_would_convert';

/**
 * Refusals the owner cannot fix herself: Greenback support moves the group,
 * so the dialog offers "Contact us" beside the server's sentence.
 */
export const MOVE_CONTACT_REFUSALS: readonly string[] = [
  NO_CARD_SUBSCRIPTION,
  STRIPE_CREDIT_BALANCE,
  COMP_WOULD_CONVERT,
  FIRM_BILLED,
  ISSUER_NOT_CONFIGURED,
  REMITTANCE_NOT_CONFIGURED,
  UNPRICED_LINE,
  NOT_PURCHASABLE,
  OFF_STRIPE_MODULE_REQUIRED,
];

/** Refusals fixed on the card, in Manage billing: the dialog leads to billing. */
export const MOVE_CARD_REFUSALS: readonly string[] = [CARD_SUBSCRIPTION_UNSETTLED, CARD_PAST_DUE];


// ---------------------------------------------------------------------------
// PRD 112 Wave 3b: paying a platform invoice by card (§4.18, PLAN-3b §6–§7).
// The owner of a group payer presses "Pay $X by card"; the api starts an
// attempt and hands back the gateway's hosted-page address and a one-time
// form token; the browser POSTs that token to the gateway in a plain form, a
// full-page navigation (no iframe, no gateway script on our page: SAQ A).
// The card is typed only there. The return to the billing page proves
// nothing: only the api's read of the attempt says paid.
//
// SHIPS DARK. Every field is optional, and absent means no button: an api
// before 3b never sends `pay_by_card`, so nothing here is ever drawn or sent.
//
// THE TOKEN AND THE POST URL ARE NEVER PUT IN A URL OR LOGGED. They live in
// memory for the one form post and nowhere else.
// ---------------------------------------------------------------------------

/** `invoicing.pay_by_card` (PLAN-3b §6). */
export interface BillingPayByCard {
  /** The rail is configured and not paused, and the viewer is the payer (D16). */
  available: boolean;
  /** "v1.2" while the account has not accepted the card terms; null once it has. */
  terms_version_required: string | null;
  /** What the charge reads as on a card statement; null when unavailable. */
  descriptor: string | null;
}

/** An invoice's latest standing card payment, masked (PLAN-3b §6). */
export interface PaidByCard {
  brand: string;
  last4: string;
  /** The gateway's transaction id, masked: "••••1676". */
  transaction: string;
}

/**
 * A card attempt's status (PLAN-3b D4). `expired`: its token lapsed but a
 * last-second payment can still land, so it is still unpaid, not failed.
 * A status this web does not know reads as still checking.
 */
export type CardAttemptStatus = 'started' | 'expired' | 'paid' | 'declined' | 'abandoned';

/** GET /business/billing/card_attempts/:id (payer only). */
export interface CardAttempt {
  id: string;
  status: CardAttemptStatus;
  invoice_id: string;
  invoice_number: string;
  amount_cents: number;
  card_brand: string | null;
  card_last4: string | null;
  paid_at: string | null;
  /** Where the receipt went: the billing email. */
  receipt_email: string | null;
  /** The gateway's reason bucket for a decline (declined, avs, cvv, fraud, error); never card data. */
  decline_category: string | null;
}

/** POST /business/billing/invoices/:id/card_payment (201). */
export interface CardPaymentStart {
  attempt: { id: string; status: CardAttemptStatus; amount_cents: number; invoice_number: string };
  /** Posted to, never navigated to by URL: Accept Hosted takes the token by POST. */
  gateway: { post_url: string; token: string; expires_at: string | null };
}

/** The card terms a `terms_required` refusal carries in `details` (PLAN-3b D12). */
export interface CardTermsRequired {
  version: string;
  text: string;
  issuer_name: string;
}

// The card payment's refusals (PLAN-3b §6). The sentence shown is always the
// server's. `terms_required` and `terms_changed` are the constants above.

/** The card rail is not configured, or paused (409). */
export const CARD_NOT_CONFIGURED = 'card_not_configured';
/** Not the owner of a group payer, or a firm pays (403). */
export const NOT_PAYER = 'not_payer';
/** The invoice is paid, void or uncollectible (409). */
export const INVOICE_NOT_OPEN = 'invoice_not_open';
/** Nothing is left to pay on it (409). */
export const NOTHING_DUE = 'nothing_due';
/** The invoice's issuer is not the one the card rail collects for (409). */
export const ISSUER_MISMATCH = 'issuer_mismatch';
/** The gateway could not be reached; nothing was charged (502). */
export const GATEWAY_UNAVAILABLE = 'gateway_unavailable';
/** A 201 without a usable post address and token: nothing to send the browser to. */
export const GATEWAY_NOT_RETURNED = 'gateway_not_returned';

/**
 * Refusals after which the button the payer pressed is out of date (the
 * invoice was paid or voided meanwhile, the rail was paused, the payer
 * changed): the card re-reads, so the button goes.
 */
export const CARD_STALE_REFUSALS: readonly string[] = [
  CARD_NOT_CONFIGURED,
  NOT_PAYER,
  INVOICE_NOT_OPEN,
  NOTHING_DUE,
  ISSUER_MISMATCH,
];

/**
 * Whether this viewer may pay this account's invoices by card: the api says
 * so (`pay_by_card.available`, the payer only) and she manages the account.
 * False for an absent block (an older api), so the button never shows early.
 */
export const cardPaymentAvailable = (invoicing: BillingInvoicing | null | undefined): boolean =>
  invoicing?.pay_by_card?.available === true && invoicing.can_manage === true;

/**
 * Whether "Pay $X by card" goes on this invoice: the api says it is card
 * payable, and it is open with a balance (belt and braces: a paid, void or
 * uncollectible invoice never gets the button, whatever a payload says).
 */
export const invoiceCardPayable = (
  invoicing: BillingInvoicing | null | undefined,
  invoice: PlatformInvoiceSummary
): boolean =>
  cardPaymentAvailable(invoicing)
  && invoice.card_payable === true
  && invoice.status === 'open'
  && invoice.balance_cents > 0;

/** The card terms a `terms_required` refusal sent, or null when its details are not that shape. */
export const cardTermsFromRefusal = (details: unknown): CardTermsRequired | null => {
  const d = refusalDetails(details);
  return typeof d.version === 'string' && typeof d.text === 'string' && typeof d.issuer_name === 'string'
    ? { version: d.version, text: d.text, issuer_name: d.issuer_name }
    : null;
};

// ---------------------------------------------------------------------------
// PRD 112 Wave 6: the cannabis package (PLAN-6 §5.2, D1–D12). A signed-in
// person with no books signs up for Greenback Business plus Cannabis and 280E
// on /business/start?package=cannabis. The api creates the business, restricts
// it, opens the billing entity's account with the terms accepted in the same
// act, and issues the first invoice. Nothing here reaches Stripe or takes a
// card: the person pays the invoice by transfer (or, once Wave 3b's card rail
// exists, by card on the confirmation).
//
// DARK UNTIL THE API SAYS SO. The form is drawn only when the quote answers
// `available: true`. An api without the endpoint answers 404, which reads as
// `reason: "not_served"`: the page then says "That package isn't available
// here yet." exactly as it did before this wave.
// ---------------------------------------------------------------------------

/** The package `/business/start?package=` names for this signup. */
export const CANNABIS_PACKAGE = 'cannabis';

/** Why the package is not offered. `not_served` is this web's: an api without the endpoint (404). */
export type PackageUnavailableReason =
  | 'not_on_sale'
  | 'issuer_not_configured'
  | 'remittance_not_configured'
  | 'unpriced_line'
  | 'not_served';

/** One line of the first invoice, priced by the api's composer. Periods are platform dates. */
export interface PackageInvoiceLine {
  description: string;
  period_start: string | null;
  period_end: string | null;
  amount_cents: number;
}

/** The first invoice the signup would issue today (PLAN-6 D7, D9). */
export interface PackageFirstInvoice {
  kind: 'first';
  issued_on: string;
  due_on: string;
  /** The first read-only day if it is still unpaid (due + the 3-day window + 1). */
  read_only_from: string;
  lines: PackageInvoiceLine[];
  total_cents: number;
}

/** GET /business/cannabis/package (user-scoped; no X-Business-Id). Pure: it writes nothing. */
export interface PackageQuote {
  available: boolean;
  reason: PackageUnavailableReason | null;
  /** The billing entity's legal name: who invoices. Null when unavailable. */
  issuer_name: string | null;
  /** Business + Cannabis and 280E for one entity, per month. */
  monthly_cents: number | null;
  /** Each additional licensed entity, per month. */
  additional_entity_cents: number | null;
  /** The api's vocabulary, in its order. */
  operator_types: { key: string; label: string }[];
  /** Null when unavailable. */
  first_invoice: PackageFirstInvoice | null;
  /** Verbatim, rendered with the issuer's name. Null when unavailable. The version is echoed, never the text. */
  terms: { version: string; text: string } | null;
  /** Wave 3b's card rail is configured for this signup's issuer. False in Wave 6. */
  card_available: boolean;
  /** The person already owns a business: the App Shop pointer (D12). */
  owns_business: boolean;
}

/** POST /business/cannabis/signup's body. The echo fields are what the person was shown. */
export interface PackageSignupBody {
  operator_type: string;
  name: string;
  state_code: string;
  entity_type: string;
  billing_email: string;
  terms_version: string;
  first_invoice_total_cents: number;
  first_invoice_issued_on: string;
  /** Generated once per person and package, kept for the session (D7). */
  signup_token: string;
}

/** The first invoice as the signup issued it. */
export interface PackageIssuedInvoice {
  id: string;
  number: string;
  total_cents: number;
  issued_on: string;
  due_on: string;
  /** 3 for a first invoice (CONTRACT-CHANGES-6 item 8). */
  window_days: number;
  /** `due_on + window_days + 1`, as the api worked it out. */
  read_only_from: string;
  lines: PackageInvoiceLine[];
}

/** POST /business/cannabis/signup → 201. */
export interface PackageSignupResult {
  /** Byte for byte POST /businesses's payload (item 13), `restricted_industry` and `entity_group_id` included. */
  business: { id: string; name: string; entity_group_id?: string | null; restricted_industry?: string | null };
  entity_group_id: string;
  invoice: PackageIssuedInvoice;
  /**
   * The new account's BillingStatus (CONTRACT-CHANGES-6): the invoicing
   * block ITSELF, as GET /business/billing's `invoicing` (billing email,
   * remittance with the fraud line, terms), not wrapped in a billing payload.
   */
  billing: BillingInvoicing | null;
  card_available: boolean;
  /** "/business/cannabis/setup?operator_type=dispensary". */
  setup_path: string;
}

/**
 * `details` of 409 `already_invoiced`: this person's token was used already
 * (D7). `invoice` is null when an admin voided the first invoice, and
 * `setup_path` (from the chart) null for a chart that is not cannabis (item 9).
 */
export interface PackageAlreadyInvoicedDetails {
  business_id: string | null;
  entity_group_id: string;
  invoice: { id: string; number: string; total_cents: number; due_on: string | null } | null;
  setup_path: string | null;
}

/** `details` of 409 `package_exists`: the person already has an open package account (Q4). */
export interface PackageExistsDetails {
  business_id: string;
  entity_group_id: string;
}

// The signup's refusal codes (PLAN-6 §5.2). The page branches on the code,
// never the sentence. `not_on_sale`, `issuer_not_configured`,
// `remittance_not_configured`, `unpriced_line`, `terms_changed`,
// `quote_changed`, `acknowledgement_required`, `invalid_email` and
// `stripe_unreachable` are defined above.

/** The gate is closed: prod before release (403). */
export const NOT_ON_SALE = 'not_on_sale';
/** This person's token was used already: the confirmation from `details` (409). */
export const ALREADY_INVOICED = 'already_invoiced';
/** An open package account already exists for this person (409, Q4). */
export const PACKAGE_EXISTS = 'package_exists';
/** The reused signup group has a live card subscription: support moves it (409). */
export const STRIPE_SUBSCRIPTION_LIVE = 'stripe_subscription_live';
/** The reused signup group was filled by another request (409): asking again uses a new one. */
export const GROUP_FILLED = 'group_filled';
/**
 * Her unfilled signup group is already paid for another way, or already on
 * invoicing (409, item 1; `details.entity_group_id`): she names it first.
 * Never retried.
 */
export const PAID_GROUP_WAITING = 'paid_group_waiting';
/** The business could not be marked restricted; nothing was created (409, item 2). */
export const RESTRICTION_FAILED = 'restriction_failed';
/** Field errors (422): `details` is `[{ field, message }]`. */
export const VALIDATION_FAILED = 'validation_failed';
/** More than five a minute (429). */
export const RATE_LIMITED = 'rate_limited';

/** Refusals that mean the package is not offered here: the page says "Not available yet". */
export const PACKAGE_UNAVAILABLE_REFUSALS: readonly string[] = [
  NOT_ON_SALE,
  ISSUER_NOT_CONFIGURED,
  REMITTANCE_NOT_CONFIGURED,
  UNPRICED_LINE,
];

/** Refusals that mean the quote or terms she accepted are not the current ones: re-read, ask again. */
export const PACKAGE_REQUOTE_REFUSALS: readonly string[] = [QUOTE_CHANGED, TERMS_CHANGED, ACKNOWLEDGEMENT_REQUIRED];

// ---------------------------------------------------------------------------
// PRD 112 Wave 5: a firm paying for a client on Greenback's own invoice
// (§4.6.6, §4.7, PLAN-5 §6–§7). A firm-paid client that holds an off-Stripe
// module is billed by invoice like any other such client; the firm keeps its
// card for every other client. The billing entity invoices the firm monthly,
// one invoice listing every client it pays for, and the firm owner pays it by
// card on /firm/billing or by transfer.
//
// SHIPS DARK, AND ABSENT MEANS NOTHING TO DRAW. An api before Wave 5 sends no
// `invoicing` on GET /firm/billing, no `rail` on a client or a module, and no
// `confirmation` on a module preview, so /firm/billing and the modules drawer
// render exactly as before and nothing here is ever sent.
//
// MONEY IS INTEGER CENTS AND DATES ARE PLATFORM DATES (YYYY-MM-DD, Eastern):
// both go through billing/invoiceFormat, never `new Date("YYYY-MM-DD")`.
// Nothing about the firm (its invoice, its wholesale figure) is ever on a
// client's payload (PLAN-5 D13).
// ---------------------------------------------------------------------------

/** One client on a firm invoice: its share of the invoice, at wholesale, with retail beside. */
export interface FirmInvoiceClient {
  /** The engagement; null if it has ended since (the invoice still names the client). */
  firm_client_id: string | null;
  name: string;
  amount_cents: number;
  balance_cents: number;
  /** What the client would pay at retail; null when the api sends none. */
  retail_cents: number | null;
  /**
   * The last day this client stays on the firm's bill if the firm leaves it
   * unpaid (§4.7.3): after it, billing for the client moves to the client.
   * Null until a payment window has started.
   */
  lapse_on: string | null;
}

/** One firm invoice (PLAN-5 §6), grouped by client. */
export interface FirmPlatformInvoice {
  id: string;
  number: string;
  kind: PlatformInvoiceKind;
  status: PlatformInvoiceStatus;
  period_start: string;
  period_end: string;
  issued_on?: string;
  due_on: string;
  total_cents: number;
  balance_cents: number;
  /**
   * Credited back to the firm on this invoice (a client handed its own billing
   * after a firm lapse, PLAN-5 Q2): already out of `balance_cents`. Absent or 0: none.
   */
  credited_cents?: number;
  window_days?: number;
  /** Today is after `due_on` and a balance is left (the api's call). Absent reads as false. */
  past_due?: boolean;
  /** Null for anyone who may not download it. */
  pdf_path: string | null;
  clients: FirmInvoiceClient[];
  card_payable?: boolean;
  live_card_attempt?: { id: string; status: CardAttemptStatus } | null;
  paid_by_card?: PaidByCard | null;
}

/** Which firm terms the account accepted, and which one a card payment still needs. */
export interface FirmBillingTerms {
  /** The version that stands (accepted); null while none does. */
  version: string | null;
  accepted_at: string | null;
  /** "v1.2-firm" while the next card payment must accept it (or any version while none stands); null otherwise. */
  required_version: string | null;
  /** `required_version`'s text, verbatim with the issuer's name; null when nothing is required. */
  text?: string | null;
}

/** GET /firm/billing's `invoicing` block (PLAN-5 §6, D10). Null when the firm has no platform account. */
export interface FirmInvoicing {
  account_id: string;
  /** The billing entity's legal name: who invoices the firm. */
  issuer_name: string;
  billing_email: string | null;
  terms?: FirmBillingTerms | null;
  /** Open invoices, the oldest due first. */
  open_invoices: FirmPlatformInvoice[];
  /** Paid, void or uncollectible, newest first. */
  recent_invoices: FirmPlatformInvoice[];
  unapplied_credit_cents: number;
  /** The firm owner gets the full bank details; an admin or staff member the masked line. Null when unset. */
  remittance: RemittanceDetails | null;
  /** The firm owner only (D11): absent for anyone else, and from an api before the card. */
  pay_by_card?: BillingPayByCard | null;
}

/** Whether the api sent the firm's invoicing block (Wave 5). False for null, absent or a malformed block. */
export const hasFirmInvoicing = (invoicing: FirmInvoicing | null | undefined): invoicing is FirmInvoicing =>
  invoicing != null && Array.isArray(invoicing.open_invoices) && Array.isArray(invoicing.recent_invoices);

/** The firm owner may pay the firm's invoices by card: the api says so. False when absent. */
export const firmCardPaymentAvailable = (invoicing: FirmInvoicing | null | undefined): boolean =>
  invoicing?.pay_by_card?.available === true;

/** "Pay $X by card" on this firm invoice: the api says so, and it is open with a balance. */
export const firmInvoiceCardPayable = (
  invoicing: FirmInvoicing | null | undefined,
  invoice: FirmPlatformInvoice
): boolean =>
  firmCardPaymentAvailable(invoicing)
  && invoice.card_payable === true
  && invoice.status === 'open'
  && invoice.balance_cents > 0;

/** A client billed by invoice: the api's per-client rail on GET /firm/billing (D10). */
export type FirmClientInvoicePayer = 'firm' | 'client';

/** The fields Wave 5 adds to each of GET /firm/billing's `clients[]`. All optional: absent is today. */
export interface FirmBillingClientRailFields {
  /** Null for a client without books (CONTRACT-CHANGES-5 §5). */
  rail?: BillingRail | null;
  /** Who pays Greenback's invoice for this client; null on the card rail. */
  invoice_payer?: FirmClientInvoicePayer | null;
  /** "Pay for this client" is offered: invoiced, pays itself, and the viewer is the firm owner (Q4). */
  can_pay_for?: boolean;
}

/** One line the confirmation lists: at wholesale, with retail beside. */
export interface FirmInvoiceConfirmationLine {
  description: string;
  amount_cents: number;
  retail_cents: number | null;
}

/**
 * What moving a client onto the firm's invoice means, priced by the api's
 * composer (§4.7.2, PLAN-5 D3). The copy is built from these fields and the
 * module's name, never a literal (PRD 42 Rule 1).
 */
export interface FirmInvoiceConfirmation {
  issuer_name: string;
  /** The off-Stripe module being added; absent for "Pay for this client". */
  module_name?: string | null;
  /** The client, when the api names it. */
  client_name?: string | null;
  /** Everything the client holds, a month, at wholesale. */
  monthly_wholesale_cents: number | null;
  monthly_retail_cents: number | null;
  lines: FirmInvoiceConfirmationLine[];
  first_invoice: { total_cents: number; starts_on: string; issued_on: string; due_on: string } | null;
  /** The client was the firm's last card-paid seat: the card ends and the invoice starts then. */
  last_seat: boolean;
  card_ends_on: string | null;
  /** The terms the firm must accept, verbatim; null once it has accepted them. */
  terms: { version: string; text: string } | null;
  /** The firm owner, for an admin's "Ask <owner> to confirm". Absent: "the practice owner". */
  owner_name?: string | null;
}

/**
 * GET /firm/clients/:id/modules/preview. An api before Wave 5, and any
 * Stripe-rail module, sends only `monthly_increase_cents`; an off-Stripe
 * module sends `rail: "invoice"` and the confirmation.
 */
export interface FirmModulePreview {
  monthly_increase_cents?: number | null;
  /** `card` or `invoice` (CONTRACT-CHANGES-5 §2). */
  rail?: 'card' | 'invoice' | 'stripe';
  confirmation?: FirmInvoiceConfirmation | null;
}

/** The figures the confirm echoes, so the firm is never billed a number it did not see. */
export const confirmationEcho = (
  preview: { monthly_increase_cents?: number | null },
  confirmation: FirmInvoiceConfirmation
): { monthlyIncreaseCents: number | null; firstInvoiceTotalCents: number | null } => ({
  monthlyIncreaseCents: preview.monthly_increase_cents ?? confirmation.monthly_wholesale_cents ?? null,
  firstInvoiceTotalCents: confirmation.first_invoice?.total_cents ?? null,
});

/** GET /firm/clients/:id/invoice_payer (Q4): "Pay for this client", priced. Pure. */
export interface FirmInvoicePayerPreview {
  monthly_increase_cents?: number | null;
  /**
   * The first day the firm pays (CONTRACT-CHANGES-5 §4): the day after the
   * client's own invoiced-until, not today. Null when the api sends none.
   */
  starts_on?: string | null;
  /** The viewer is the firm owner, who confirms. Absent reads as false. */
  can_confirm?: boolean;
  /** The first invoice the take-over would issue; null when nothing is billable yet. */
  first_invoice?: { total_cents: number } | null;
  /**
   * The request already waiting on the client (Dave, 2026-10-24: the client
   * agrees first). Null or absent: none, and the owner may send one.
   */
  pending_request?: FirmTakeoverRequest | null;
  confirmation: FirmInvoiceConfirmation;
}

/**
 * A firm's request to pay for a client that pays its own invoice, waiting on
 * the client's owner (CONTRACT: api `2425ac7b5`, W5 consent a). Nothing moves
 * until she accepts; it lapses after 14 days.
 */
export interface FirmTakeoverRequest {
  id: string;
  kind?: 'invoice_takeover';
  /** The day the firm would start paying, as the request was quoted (W5 consent c). */
  starts_on?: string | null;
  status: string;
  awaiting: string;
  /** An instant (UTC). */
  expires_at: string;
  monthly_increase_cents: number | null;
  first_invoice_total_cents: number | null;
}

/**
 * The first invoice total "Pay for this client" echoes (W5 review 3): the
 * preview's own `first_invoice` when it sent the key, else the
 * confirmation's; null when there is none, which is echoed as null.
 */
export const payerFirstInvoiceEcho = (preview: FirmInvoicePayerPreview): number | null =>
  preview.first_invoice !== undefined
    ? preview.first_invoice?.total_cents ?? null
    : preview.confirmation.first_invoice?.total_cents ?? null;

/** What a confirm returns (201): the invoice issued to the firm, when one was. */
export interface FirmInvoiceConfirmResult {
  invoice: { id: string; number: string; total_cents: number; due_on: string } | null;
  /** "Pay for this client" before consent: the first day the firm pays. */
  starts_on?: string | null;
  /** "Pay for this client" since consent (W5 consent a): the request sent to the client. */
  request?: FirmTakeoverRequest | null;
}

// Refusal codes Wave 5 adds (PLAN-5 §6). The sentence shown is the server's.

/** Only the firm owner confirms (403). */
export const FIRM_OWNER_REQUIRED = 'firm_owner_required';
/** The client pays Greenback itself (409). */
export const CLIENT_BILLED = 'client_billed';
/** The engagement is not active (409). */
export const NOT_ACTIVE = 'not_active';
/** The firm's card is in grace: no adds until it is fixed (409; Dave 2026-10-21 rule 3). */
export const FIRM_IN_GRACE = 'firm_in_grace';
/** The module is not released (422). */
export const NOT_RELEASED = 'not_released';
/** Confirm sent without `rail_confirmed` (422). */
export const RAIL_CONFIRMATION_REQUIRED = 'rail_confirmation_required';
/**
 * "Pay for this client" while the client still owes on its own invoices (409;
 * `details.invoice_numbers`): it pays them first (CONTRACT-CHANGES-5 §4).
 */
export const CLIENT_OWES = 'client_owes';
/** A module a Greenback comp holds on an invoiced client: the comp runs on (409; CONTRACT-CHANGES-5 deviation 6). */
export const COMP_HELD = 'comp_held';

/** "Pay for this client" sent twice by this firm: one request waits already (409). */
export const ALREADY_REQUESTED = 'already_requested';
/** Another firm's request to pay for this client is waiting on it (409). */
export const TAKEOVER_PENDING = 'takeover_pending';
/** A firm may remove only the modules it added; this one the client bought itself (409, W5 consent b). */
export const CLIENT_OWNED_MODULE = 'client_owned_module';

/**
 * A client's pending billing transfer, with the kind the api adds (W5 consent
 * a): `invoice_takeover` is a firm asking to pay a client's own Greenback
 * invoice. No figure rides on it: what the firm pays is the firm's (D13).
 */
export type ClientTransferKind = 'invoice_takeover' | 'arrangement';

/** The api's extras on a client's billing transfer (W5 consent a, c). */
export interface ClientTransferTakeoverFields {
  kind?: ClientTransferKind;
  /** An `invoice_takeover`: the day the firm would start paying, as quoted. No figure (D13). */
  starts_on?: string | null;
}

/** A firm's request to take over this client's invoice, waiting on the client. False for an older api. */
export const isTakeoverRequest = (transfer: { kind?: string; awaiting: string; status: string }): boolean =>
  transfer.kind === 'invoice_takeover' && transfer.awaiting === 'client' && transfer.status === 'pending';

/** The invoice numbers a `client_owes` refusal names, or none. */
export const owedInvoiceNumbers = (details: unknown): string[] => {
  const numbers = refusalDetails(details).invoice_numbers;
  return Array.isArray(numbers) ? numbers.filter((n): n is string => typeof n === 'string') : [];
};

/**
 * Refusals after which the confirmation on screen is out of date (the price,
 * the first invoice or the terms moved): it re-reads the preview and asks
 * again, with the server's sentence over it.
 */
export const CONFIRMATION_REQUOTE_REFUSALS: readonly string[] = [
  TERMS_CHANGED,
  TERMS_REQUIRED,
  ACKNOWLEDGEMENT_REQUIRED,
  QUOTE_CHANGED,
];
